/**
 * Cost estimator.
 *
 * Walks a PipelineDoc, looks each module up in the manifest, evaluates
 * its cost_model against the provided TargetMetrics, and returns a
 * PipelineEstimate. Modules whose calibration is incomplete (rate values
 * null OR confidence === 0) surface as "estimate unavailable" — they are
 * NEVER silently zeroed. Matches docs/constantine-module-metadata-spec.md.
 *
 * Driver evaluation uses `expr-eval` (~10KB, MIT, zero deps) — ceil / floor
 * / min / max / abs / count / + - * / / % / parens / nested member access
 * are all supported out of the box. Variables are looked up in a nested
 * scope keyed by namespace (target.*, module.*, input.*).
 *
 * Chain evaluation: we walk the pipeline in stage order BEFORE the cost
 * pass. For each module that declares `emits.<key>`, we evaluate the
 * driver, multiply by rate, and add the result into a running map at
 * `input.<key>`. Subsequent modules (in later stages OR later in the
 * same stage's module list) see the accumulated value via input.<key>
 * in their cost_model.driver expressions. Generic: any module emits
 * any number of typed keys (`bytes`, `findings_count`, ...).
 */

import { Parser, type Value } from 'expr-eval';

import { tokensToCost } from './modelPricing';
import type {
  ComponentEstimate,
  CostLeaf,
  ModuleManifest,
  ModuleRef,
  PipelineDoc,
  PipelineEstimate,
  PipelineManifest,
  StageDoc,
  StageEstimate,
  TargetMetrics,
} from './types';
import { getModuleRefName, toModuleRefObject } from './types';

/**
 * Default target metrics used when the caller doesn't supply real asset
 * stats. Calibrated rates assume FILTERED bytes (post `should_reject_file`
 * + NUL-sniff). Empirical mean filter-retention ratio across a 6-repo
 * constantine corpus is 0.21 (range 0.03 — 0.60, median 0.09); when the
 * `constantine target-metrics` preflight CLI is wired into the asset-ingest
 * path, the caller passes already-filtered bytes and the editor doesn't
 * need to scale.
 */
const DEFAULT_TARGET_METRICS: Required<
  Pick<TargetMetrics, 'loc' | 'file_count' | 'function_count' | 'size_bytes'>
> = {
  loc: 50_000,
  file_count: 500,
  function_count: 5_000,
  size_bytes: 5_000_000,
};

/**
 * Confidence → relative spread for uncertainty bounds. A leaf with
 * confidence c surfaces as [p50 × (1 − s/2), p50, p50 × (1 + s/2)]
 * where s = 1 − c. Clamps the half-spread to 0.50 so the worst case
 * still gives a bounded range. Used when calibration.observed_p10_p90_ratio
 * is absent (newly calibrated rate with no observed variance data yet).
 */
function spreadFromConfidence(confidence: number): number {
  return Math.min(0.5, Math.max(0, 1 - confidence) / 2);
}

/**
 * Compute (low, high) bounds around a p50 cost estimate. Prefers
 * calibration.observed_p10_p90_ratio when present (log-normal-ish
 * empirical spread); falls back to confidence-derived linear spread
 * otherwise.
 *
 * Why log-normal: cost distributions are right-skewed (a tail of
 * expensive runs drags the mean up). Multiplicative bounds match
 * better than additive ones — a 2.6× p90/p10 ratio yields a [0.62×,
 * 1.61×] band around p50, which matches the observed $25-$65 range
 * for cwe-fanout's $34 p50 better than ±30% additive would.
 */
function leafBounds(
  p50: number,
  confidence: number,
  observedRatio: number | undefined
): { low: number; high: number } {
  if (observedRatio && observedRatio > 1) {
    const half = Math.sqrt(observedRatio);
    return { low: p50 / half, high: p50 * half };
  }
  const s = spreadFromConfidence(confidence);
  return { low: p50 * (1 - s), high: p50 * (1 + s) };
}

// expr-eval Parser singleton. allowMemberAccess enables `target.size_bytes`
// style dotted lookups against the nested scope object.
const parser = new Parser({ allowMemberAccess: true });

interface EvalContext {
  target: TargetMetrics;
  /** module.* lookups resolve against the active module's effective config. */
  moduleConfig: Record<string, unknown>;
  /** pipeline.llm.models — used to resolve model_tier when no model_id is set. */
  pipelineModels?: Partial<Record<'fast' | 'standard' | 'deep', string>>;
  /**
   * pipeline.llm.provider — drives logical→routed model_id resolution
   * (e.g. provider='bedrock' routes bare claude-haiku to its Bedrock
   * inference-profile SKU for correct LiteLLM pricing).
   */
  provider?: string;
  /**
   * input.* namespace populated by the chain-eval walk over upstream
   * modules' `emits` fields. Keys are arbitrary (ingest modules emit
   * `bytes`; detect modules emit `findings_count`; reviewer modules
   * emit a filtered `findings_count`; etc). Values are running sums.
   * Downstream cost_model drivers read these via `input.<key>`.
   */
  input?: Record<string, number>;
}

/**
 * Evaluate a driver expression. Returns null when the expression is
 * unresolvable (unknown variable / syntax error / division by zero /
 * NaN result). Callers map null → reason='driver_unresolved'.
 */
function evalDriver(expr: string, ctx: EvalContext): number | null {
  const scope: Record<string, Value> = {
    target: ctx.target as unknown as Value,
    module: ctx.moduleConfig as unknown as Value,
    input: (ctx.input ?? {}) as unknown as Value,
  };
  try {
    const result = parser.evaluate(expr, scope);
    if (typeof result !== 'number') return null;
    if (Number.isNaN(result) || !Number.isFinite(result)) return null;
    return result;
  } catch {
    return null;
  }
}

interface LeafResult {
  low: number | null;
  p50: number | null;
  high: number | null;
  reason?: ComponentEstimate['reason'];
}

/**
 * Resolve a per_unit_ensemble's `models` field into a concrete string[].
 * The field can be:
 *   - A literal array of model_ids (legacy shape)
 *   - A `module.<knob>` reference where <knob> is an array config knob
 *   - A `module.<plural>` where the plural knob is empty but a singular
 *     `<plural-without-s>` knob is set — promoted to `[singular_value]`.
 *     Legacy fallback: constantine 7872c8a+ normalizes layer2_models at
 *     manifest-gen time so this branch shouldn't fire for current
 *     manifests. Kept as defense-in-depth for custom-uploaded YAMLs
 *     authored against older constantine versions.
 *
 * Returns null when no usable model list can be derived.
 */
function resolveEnsembleModels(
  models: unknown,
  moduleConfig: Record<string, unknown>
): string[] | null {
  if (Array.isArray(models)) {
    const strings = models.filter(
      (m: unknown): m is string => typeof m === 'string'
    );
    return strings.length > 0 ? strings : null;
  }
  if (typeof models !== 'string') return null;
  if (!models.startsWith('module.')) return null;

  const knob = models.slice('module.'.length);
  const val = moduleConfig[knob];
  if (Array.isArray(val)) {
    const strings = val.filter(
      (m: unknown): m is string => typeof m === 'string'
    );
    if (strings.length > 0) return strings;
  }
  // Empty/missing plural list → try the singular knob (strip trailing 's').
  // e.g. layer2_models empty + layer2_model: 'claude-sonnet-4-6' → ['claude-sonnet-4-6'].
  if (knob.endsWith('s')) {
    const singular = knob.slice(0, -1);
    const singularVal = moduleConfig[singular];
    if (typeof singularVal === 'string' && singularVal.length > 0) {
      return [singularVal];
    }
  }
  return null;
}

function isCalibrated(leaf: CostLeaf): boolean {
  if (leaf.type === 'fixed') {
    return leaf.amount_usd !== null && leaf.confidence > 0;
  }
  if (leaf.type === 'per_unit' || leaf.type === 'per_unit_ensemble') {
    return (
      leaf.rate.input_tokens_per_unit !== null &&
      leaf.rate.output_tokens_per_unit !== null &&
      leaf.confidence > 0
    );
  }
  if (leaf.type === 'bounded_agentic') {
    // expected_iterations can be null in real manifests (telemetry not
    // yet collected); we treat the leaf as calibrated when token rates
    // and confidence are present — driver evaluator can substitute
    // max_iterations as the upper bound when expected is missing.
    return (
      leaf.per_iteration.input_tokens_per_unit !== null &&
      leaf.per_iteration.output_tokens_per_unit !== null &&
      leaf.confidence > 0
    );
  }
  return false;
}

/**
 * Estimate a single cost leaf. Returns LeafResult with nulls when not
 * estimable. Per-unit / per_unit_ensemble / bounded_agentic resolve
 * tokens × rate × model-pricing into a USD figure with confidence-driven
 * low/high bounds. Pricing is resolved purely from the leaf's `model_id`
 * via tokensToCost; routing (Bedrock, OpenRouter) is encoded directly in
 * the model_id string (`bedrock/us.anthropic...-v1:0`) rather than as a
 * separate overlay multiplier.
 */
function estimateLeaf(leaf: CostLeaf, ctx: EvalContext): LeafResult {
  if (!isCalibrated(leaf)) {
    return { low: null, p50: null, high: null, reason: 'missing_calibration' };
  }

  if (leaf.type === 'fixed') {
    const amount = leaf.amount_usd ?? 0;
    return { low: amount, p50: amount, high: amount };
  }

  const resolveModelId = (
    rawModelId: string | undefined,
    tier: 'fast' | 'standard' | 'deep' | undefined
  ): string | null => {
    if (rawModelId) {
      // Inline driver-like references (e.g. "module.layer1_model") get
      // resolved against the active module config.
      if (rawModelId.startsWith('module.')) {
        const v = ctx.moduleConfig[rawModelId.slice('module.'.length)];
        return typeof v === 'string' && v.length > 0 ? v : null;
      }
      return rawModelId;
    }
    if (tier) {
      const v = ctx.pipelineModels?.[tier];
      return v ?? null;
    }
    return null;
  };

  if (leaf.type === 'per_unit') {
    const units = evalDriver(leaf.driver, ctx);
    if (units === null) {
      return { low: null, p50: null, high: null, reason: 'driver_unresolved' };
    }
    const modelId = resolveModelId(leaf.model_id, leaf.model_tier);
    if (!modelId) {
      return {
        low: null,
        p50: null,
        high: null,
        reason: 'missing_calibration',
      };
    }
    const inTok = (leaf.rate.input_tokens_per_unit ?? 0) * units;
    const outTok = (leaf.rate.output_tokens_per_unit ?? 0) * units;
    const cacheCreate = (leaf.rate.cache_creation_tokens_per_unit ?? 0) * units;
    const cacheRead = (leaf.rate.cache_read_tokens_per_unit ?? 0) * units;
    const p50 = tokensToCost(
      inTok,
      outTok,
      modelId,
      ctx.provider,
      cacheCreate,
      cacheRead
    );
    const { low, high } = leafBounds(
      p50,
      leaf.confidence,
      leaf.calibration?.observed_p10_p90_ratio
    );
    return { low, p50, high };
  }

  if (leaf.type === 'per_unit_ensemble') {
    const units = evalDriver(leaf.driver, ctx);
    if (units === null) {
      return { low: null, p50: null, high: null, reason: 'driver_unresolved' };
    }
    const modelList = resolveEnsembleModels(leaf.models, ctx.moduleConfig);
    if (modelList === null || modelList.length === 0) {
      return {
        low: null,
        p50: null,
        high: null,
        reason: 'ensemble_unresolved',
      };
    }
    const inTok = (leaf.rate.input_tokens_per_unit ?? 0) * units;
    const outTok = (leaf.rate.output_tokens_per_unit ?? 0) * units;
    const cacheCreate = (leaf.rate.cache_creation_tokens_per_unit ?? 0) * units;
    const cacheRead = (leaf.rate.cache_read_tokens_per_unit ?? 0) * units;
    const p50 = modelList.reduce(
      (acc, m) =>
        acc +
        tokensToCost(inTok, outTok, m, ctx.provider, cacheCreate, cacheRead),
      0
    );
    const { low, high } = leafBounds(
      p50,
      leaf.confidence,
      leaf.calibration?.observed_p10_p90_ratio
    );
    return { low, p50, high };
  }

  if (leaf.type === 'bounded_agentic') {
    const modelId = resolveModelId(leaf.model_id, leaf.model_tier);
    if (!modelId) {
      return {
        low: null,
        p50: null,
        high: null,
        reason: 'missing_calibration',
      };
    }
    const maxIters =
      typeof leaf.max_iterations === 'number'
        ? leaf.max_iterations
        : evalDriver(leaf.max_iterations, ctx);
    if (maxIters === null) {
      return { low: null, p50: null, high: null, reason: 'driver_unresolved' };
    }
    // When telemetry has been published, use it; otherwise fall back to
    // max/2 as the point estimate and the full max as the upper bound.
    const expected =
      typeof leaf.expected_iterations === 'number'
        ? leaf.expected_iterations
        : typeof leaf.expected_iterations === 'string'
          ? evalDriver(leaf.expected_iterations, ctx)
          : null;
    const p50Iter = expected ?? maxIters / 2;
    const perCallIn = leaf.per_iteration.input_tokens_per_unit ?? 0;
    const perCallOut = leaf.per_iteration.output_tokens_per_unit ?? 0;
    const perCallCacheCreate =
      leaf.per_iteration.cache_creation_tokens_per_unit ?? 0;
    const perCallCacheRead = leaf.per_iteration.cache_read_tokens_per_unit ?? 0;
    const p50 = tokensToCost(
      perCallIn * p50Iter,
      perCallOut * p50Iter,
      modelId,
      ctx.provider,
      perCallCacheCreate * p50Iter,
      perCallCacheRead * p50Iter
    );
    const { low, high } = leafBounds(
      p50,
      leaf.confidence,
      leaf.calibration?.observed_p10_p90_ratio
    );
    return { low, p50, high };
  }

  return { low: null, p50: null, high: null, reason: 'missing_calibration' };
}

/**
 * Build the effective module config for evaluation. Layers, lowest priority
 * to highest:
 *   1. config_knobs[*].default from the manifest (e.g. layer1_chunk_size: 5000)
 *   2. doc.module_config[moduleName] overrides set on the pipeline doc
 *   3. ModuleRefObject inline overrides (per-stage)
 *
 * Without this, driver expressions like `module.layer1_chunk_size` resolve
 * to undefined → NaN → estimate-unavailable, even though the manifest
 * declares a default. Matches constantine's run-side behavior where each
 * module's runtime config starts from `config_knobs[*].default` and is
 * overlaid with pipeline-yaml + ref-level fields.
 */
function buildModuleConfig(
  moduleManifest: ModuleManifest,
  doc: PipelineDoc,
  refObj: { name: string; [k: string]: unknown }
): Record<string, unknown> {
  const fromKnobs: Record<string, unknown> = {};
  for (const [knobName, knob] of Object.entries(
    moduleManifest.config_knobs ?? {}
  )) {
    if (knob && knob.default !== undefined && knob.default !== null) {
      fromKnobs[knobName] = knob.default;
    }
  }
  return {
    ...fromKnobs,
    ...(doc.module_config?.[moduleManifest.name] ?? {}),
    ...refObj,
  };
}

function estimateModule(
  stageName: string,
  moduleRef: ModuleRef,
  manifest: PipelineManifest,
  doc: PipelineDoc,
  target: TargetMetrics,
  input: Record<string, number> | undefined
): ComponentEstimate[] {
  const refObj = toModuleRefObject(moduleRef);
  const moduleName = refObj.name;
  const moduleManifest: ModuleManifest | undefined =
    manifest.modules[moduleName];

  if (!moduleManifest) {
    return [
      {
        module: moduleName,
        stage: stageName,
        low: null,
        p50: null,
        high: null,
        reason: 'no_cost_model',
      },
    ];
  }

  const moduleConfig = buildModuleConfig(moduleManifest, doc, refObj);

  // Per-module llm override dispatch order (constantine-agent msg #136):
  //   1. ModuleRefObject.llm (per-module override) — highest priority
  //   2. doc.llm (top-level) — fallback
  // Pricing must follow runtime semantics: if the user pinned a
  // cross-provider model on this module via an `llm:` block, project the
  // module's cost against THAT model's pricing, not the top-level one.
  const moduleLlm = refObj.llm;
  const pipelineModels = moduleLlm?.models ?? doc.llm?.models;
  const provider = moduleLlm?.provider ?? doc.llm?.provider;

  const ctx: EvalContext = {
    target: applyModuleFilter(target, moduleManifest.filter_id),
    moduleConfig,
    pipelineModels,
    provider,
    input,
  };

  if (moduleManifest.cost_model.type === 'composite') {
    return moduleManifest.cost_model.components.map(component => {
      const result = estimateLeaf(component, ctx);
      return {
        module: moduleName,
        stage: stageName,
        componentName: component.name,
        low: result.low,
        p50: result.p50,
        high: result.high,
        reason: result.reason,
      };
    });
  }

  const result = estimateLeaf(moduleManifest.cost_model, ctx);
  return [
    {
      module: moduleName,
      stage: stageName,
      low: result.low,
      p50: result.p50,
      high: result.high,
      reason: result.reason,
    },
  ];
}

function estimateStage(
  stage: StageDoc,
  manifest: PipelineManifest,
  doc: PipelineDoc,
  target: TargetMetrics,
  input: Record<string, number> | undefined
): StageEstimate {
  const components: ComponentEstimate[] = [];
  const unavailable: ComponentEstimate[] = [];

  for (const mod of stage.modules) {
    for (const est of estimateModule(
      String(stage.name),
      mod,
      manifest,
      doc,
      target,
      input
    )) {
      if (est.low === null || est.p50 === null || est.high === null) {
        unavailable.push(est);
      } else {
        components.push(est);
      }
    }
  }

  return {
    stage: String(stage.name),
    low: components.reduce((acc, c) => acc + (c.low ?? 0), 0),
    p50: components.reduce((acc, c) => acc + (c.p50 ?? 0), 0),
    high: components.reduce((acc, c) => acc + (c.high ?? 0), 0),
    components,
    unavailable,
  };
}

/**
 * Walk the pipeline in stage order, evaluating each module's `emits`
 * field once and accumulating per-key totals into a running input map.
 * Each module sees the input.* state produced by ALL strictly-upstream
 * modules — so an ingest module's emits.bytes flows into a detect
 * module's input.bytes; that detect module's emits.findings_count then
 * flows into a reviewer's input.findings_count; and so on.
 *
 * Per spec: do NOT cap inside this walk. Downstream cost_model drivers
 * apply their own clamps (e.g. reviewer's
 * `min(input.findings_count, module.max_findings)`) — keeping the chain
 * uncapped lets every downstream consumer see the true upstream value.
 *
 * Modules with `emits[key].confidence === 0` contribute nothing for
 * that key (treated as not-yet-calibrated).
 */
function walkEmitsChain(
  doc: PipelineDoc,
  manifest: PipelineManifest,
  target: TargetMetrics
): Record<string, number> {
  const accumulated: Record<string, number> = {};

  for (const stage of doc.pipeline) {
    for (const mod of stage.modules) {
      const refObj = toModuleRefObject(mod);
      const moduleManifest = manifest.modules[refObj.name];
      if (!moduleManifest?.emits) continue;

      // Each emits.<key>.driver is evaluated against the module's own
      // config scope PLUS the accumulator-so-far as `input.*`. Reuse
      // buildModuleConfig so config_knobs[*].default seeds the scope
      // before doc.module_config and refObj overrides.
      const moduleConfig = buildModuleConfig(moduleManifest, doc, refObj);
      const ctx: EvalContext = {
        target: applyModuleFilter(target, moduleManifest.filter_id),
        moduleConfig,
        pipelineModels: doc.llm?.models,
        provider: doc.llm?.provider,
        // Snapshot the accumulator BEFORE this module's contributions
        // — a module's own emits driver can't reference its own output.
        input: { ...accumulated },
      };

      for (const [key, emit] of Object.entries(moduleManifest.emits)) {
        if (!emit || emit.confidence === 0) continue;
        const units = evalDriver(emit.driver, ctx);
        if (units === null) continue;
        accumulated[key] = (accumulated[key] ?? 0) + units * emit.rate;
      }
    }
  }
  return accumulated;
}

export function findUnknownModules(
  doc: PipelineDoc,
  manifest: PipelineManifest
): string[] {
  const unknown: string[] = [];
  for (const stage of doc.pipeline) {
    for (const mod of stage.modules) {
      const name = getModuleRefName(mod);
      if (!manifest.modules[name]) unknown.push(name);
    }
  }
  return unknown;
}

/**
 * Resolve the effective TargetMetrics used for driver evaluation.
 * No raw→filtered scaling here — that was the 0.21 fudge constantine
 * shipped `dist/filter-rules.json` to replace. Modules with `filter_id`
 * are switched to their filter's exact post-filter bytes inside
 * `estimateModule`; modules without `filter_id` drive on raw size_bytes.
 */
function resolveTargetMetrics(supplied: TargetMetrics): TargetMetrics {
  return { ...DEFAULT_TARGET_METRICS, ...supplied };
}

/**
 * Per-module override: if the module declares a `filter_id` and the
 * caller-supplied target carries `filtered_bytes_by_filter`, swap
 * `target.size_bytes` to the matching entry for this module's driver
 * context. Pass-through modules (no filter_id) keep the raw size.
 */
function applyModuleFilter(
  target: TargetMetrics,
  filterId: string | undefined
): TargetMetrics {
  if (!filterId) return target;
  const map = target.filtered_bytes_by_filter;
  if (!map) return target;
  const bytes = map[filterId];
  if (typeof bytes !== 'number') return target;
  return { ...target, size_bytes: bytes };
}

export function estimatePipeline(
  doc: PipelineDoc,
  manifest: PipelineManifest,
  target: TargetMetrics = {}
): PipelineEstimate {
  const effectiveTarget = resolveTargetMetrics(target);

  // Walk the emits chain once up-front. Result is the FINAL accumulated
  // input.* state after every module's contributions have flowed
  // through. We use this as the input context for every stage's cost
  // pass — modules referencing input.<key> in their cost_model.driver
  // see the full chain value (e.g. reviewer sees the detect stage's
  // emitted findings_count).
  //
  // Trade-off: cost projections don't differentiate between "first
  // detect module sees ingest's bytes" vs "second detect module sees
  // the first detect's accumulated findings_count" — both modules see
  // the same final input.*. In practice this matches constantine's
  // runtime semantics (modules in the same stage run in parallel,
  // sharing the upstream snapshot).
  const inputCtx = walkEmitsChain(doc, manifest, effectiveTarget);

  const stages = doc.pipeline.map(stage =>
    estimateStage(stage, manifest, doc, effectiveTarget, inputCtx)
  );

  const total = {
    low: stages.reduce((acc, s) => acc + s.low, 0),
    p50: stages.reduce((acc, s) => acc + s.p50, 0),
    high: stages.reduce((acc, s) => acc + s.high, 0),
  };

  const hasUnavailable = stages.some(s => s.unavailable.length > 0);

  return {
    stages,
    total,
    budget: doc.llm.budget,
    hasUnavailable,
  };
}
